/**
 * StudySync Participant Information Collection Component
 * Collects additional participant information after Fitbit authorization
 */

import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import {
  Person,
  Email,
  Phone,
  Description,
  AttachFile,
  Visibility,
  CheckCircle,
  Cake,
  Wc,
  LocationOn,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { formatPhoneNumber } from "../../utils/textUtils";
import { GetCountries, GetState, GetCity } from "react-country-state-city";
import { analyticsService } from "../../services/analyticsService";
// Note: We're using direct fetch calls instead of apiRequest for file uploads

interface LocationOption {
  id: number;
  name: string;
}

interface ParticipantInfo {
  participantId?: string;
  studyCode?: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  age: number;
  gender: string;
  country: string;
  state: string;
  city: string;
  additionalInfo: string;
  documents: File[];
  dataSource?: 'fitbit' | 'healthkit';  // Temporary field to track selection
}

interface ParticipantInfoCollectionProps {
  participantId?: string;
  studyCode?: string;
  email?: string;
  phoneNumber?: string;
  onSuccess?: () => void; // Optional callback to proceed to next step
}

const LOCATION_SUGGESTIONS_UNAVAILABLE =
  "Location suggestions are temporarily unavailable. Enter your location manually to continue.";

const ParticipantInfoCollection: React.FC<ParticipantInfoCollectionProps> = ({
  participantId,
  studyCode,
  email: initialEmail,
  phoneNumber: initialPhoneNumber,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);

  // Location related states
  const [countries, setCountries] = useState<LocationOption[]>([]);
  const [states, setStates] = useState<LocationOption[]>([]);
  const [cities, setCities] = useState<LocationOption[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [statesLoading, setStatesLoading] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [locationDataError, setLocationDataError] = useState("");

  // Keep selection IDs separate from the values we store/submit (names)
  const [selectedCountryId, setSelectedCountryId] = useState<number | "">("");
  const [selectedStateId, setSelectedStateId] = useState<number | "">("");
  const [selectedCityId, setSelectedCityId] = useState<number | "">("");

  const [formData, setFormData] = useState<ParticipantInfo>({
    firstName: "",
    lastName: "",
    email: initialEmail || "",
    phoneNumber: initialPhoneNumber || "",
    age: 0,
    gender: "",
    country: "",
    state: "",
    city: "",
    additionalInfo: "",
    documents: [],
  });

  // Get data from location state if not provided as props
  useEffect(() => {
    if (location.state) {
      const {
        participantId: stateParticipantId,
        studyCode: stateStudyCode,
        email: stateEmail,
        phoneNumber: statePhoneNumber,
      } = location.state;
      if (stateParticipantId) {
        setFormData((prev) => ({ ...prev, participantId: stateParticipantId }));
      }
      if (stateStudyCode) {
        setFormData((prev) => ({ ...prev, studyCode: stateStudyCode }));
      }
      if (stateEmail) {
        setFormData((prev) => ({ ...prev, email: stateEmail }));
      }
      if (statePhoneNumber) {
        setFormData((prev) => ({ ...prev, phoneNumber: statePhoneNumber }));
      }
    }
  }, [location.state]);

  // Load countries on component mount
  useEffect(() => {
    const loadCountries = async () => {
      try {
        setCountriesLoading(true);
        const countriesData = await GetCountries();
        setCountries(countriesData);
        setLocationDataError("");
      } catch (err) {
        console.warn("Could not load country suggestions", err);
        setLocationDataError(LOCATION_SUGGESTIONS_UNAVAILABLE);
      } finally {
        setCountriesLoading(false);
      }
    };
    loadCountries();
  }, []);

  // Get the final values (props take precedence over state)
  const finalParticipantId = participantId || formData.participantId;
  const finalStudyCode = studyCode || formData.studyCode;
  const finalEmail = initialEmail || formData.email;
  const finalPhoneNumber = initialPhoneNumber || formData.phoneNumber;
  const emailPrefilled = Boolean(initialEmail || location.state?.email);
  const phonePrefilled = Boolean(initialPhoneNumber || location.state?.phoneNumber);
  const lockedFieldStyles = {
    "& .MuiInputBase-root": {
      backgroundColor: "action.disabledBackground",
    },
  };

  const handleInputChange =
    (field: keyof ParticipantInfo) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let value: any = event.target.value;

      if (field === "phoneNumber") {
        value = formatPhoneNumber(value);
      } else if (field === "age") {
        // Only allow integers
        value = value === "" ? 0 : parseInt(value, 10);
        if (isNaN(value)) return;
      }

      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleCountryInputChange = (value: string) => {
    setSelectedCountryId("");
    setSelectedStateId("");
    setSelectedCityId("");
    setStates([]);
    setCities([]);
    setFormData((prev) => ({
      ...prev,
      country: value,
      state: "",
      city: "",
    }));
  };

  const handleStateInputChange = (value: string) => {
    setSelectedStateId("");
    setSelectedCityId("");
    setCities([]);
    setFormData((prev) => ({
      ...prev,
      state: value,
      city: "",
    }));
  };

  const handleCityInputChange = (value: string) => {
    setSelectedCityId("");
    setFormData((prev) => ({
      ...prev,
      city: value,
    }));
  };

  const handleCountryChange = async (countryObj: LocationOption | string | null) => {
    if (typeof countryObj === "string") {
      handleCountryInputChange(countryObj);
      return;
    }

    const countryIdNum = countryObj?.id;

    setFormData((prev) => ({
      ...prev,
      country: countryObj?.name || "",
      state: "",
      city: "",
    }));

    // Reset dependent selections and lists
    setSelectedStateId("");
    setSelectedCityId("");
    setStates([]);
    setCities([]);

    if (typeof countryIdNum === "number") {
      setSelectedCountryId(countryIdNum);
      try {
        setStatesLoading(true);
        const statesData = await GetState(countryIdNum);
        setStates(statesData);
        setLocationDataError("");
      } catch (err) {
        console.warn("Could not load state suggestions", err);
        setLocationDataError(LOCATION_SUGGESTIONS_UNAVAILABLE);
      } finally {
        setStatesLoading(false);
      }
    } else {
      setSelectedCountryId("");
    }
  };

  const handleStateChange = async (stateObj: LocationOption | string | null) => {
    if (typeof stateObj === "string") {
      handleStateInputChange(stateObj);
      return;
    }

    const stateIdNum = stateObj?.id;

    setFormData((prev) => ({
      ...prev,
      state: stateObj?.name || "",
      city: "",
    }));

    // Reset dependent selection/list
    setSelectedCityId("");
    setCities([]);

    if (typeof stateIdNum === "number" && typeof selectedCountryId === "number") {
      setSelectedStateId(stateIdNum);
      try {
        setCitiesLoading(true);
        const citiesData = await GetCity(selectedCountryId, stateIdNum);
        setCities(citiesData);
        setLocationDataError("");
      } catch (err) {
        console.warn("Could not load city suggestions", err);
        setLocationDataError(LOCATION_SUGGESTIONS_UNAVAILABLE);
      } finally {
        setCitiesLoading(false);
      }
    } else {
      setSelectedStateId("");
    }
  };

  const handleCityChange = (cityObj: LocationOption | string | null) => {
    if (typeof cityObj === "string") {
      handleCityInputChange(cityObj);
      return;
    }

    const cityIdNum = cityObj?.id;

    setFormData((prev) => ({
      ...prev,
      city: cityObj?.name || "",
    }));

    if (typeof cityIdNum === "number") {
      setSelectedCityId(cityIdNum);
    } else {
      setSelectedCityId("");
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);
      setFormData((prev) => ({
        ...prev,
        documents: [...prev.documents, ...newFiles],
      }));
    }
  };

  const handleRemoveFile = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index),
    }));
  };

  const handlePreviewFile = (file: File) => {
    setPreviewFile(file);
    setPreviewDialog(true);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError("First name and last name are required");
      return;
    }

    if (!formData.age) {
      setError("Age is required");
      return;
    }

    if (!formData.gender) {
      setError("Gender is required");
      return;
    }

    if (!finalParticipantId || !finalStudyCode) {
      setError(
        "Missing participant data. Please restart the enrollment process."
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append("participantId", finalParticipantId);
      submitData.append("studyCode", finalStudyCode);
      submitData.append("firstName", formData.firstName.trim());
      submitData.append("lastName", formData.lastName.trim());

      // Append demographic/location information
      submitData.append("age", formData.age.toString());
      submitData.append("gender", formData.gender);
      submitData.append("country", formData.country);
      submitData.append("state", formData.state);
      submitData.append("city", formData.city);

      submitData.append("additionalInfo", formData.additionalInfo.trim());

      // Only append email and phoneNumber if they are provided (optional)
      if (finalEmail) {
        submitData.append("email", finalEmail.trim());
      }
      if (finalPhoneNumber) {
        submitData.append("phoneNumber", finalPhoneNumber.trim());
      }

      // Append files
      formData.documents.forEach((file, index) => {
        submitData.append(`document_${index}`, file);
      });

      // Use JWT from Fitbit callback so backend accepts update-info (avoids "Access token required")
      let authHeader: Record<string, string> = {};
      try {
        const stored = sessionStorage.getItem("studysync_participant_web_tokens");
        if (stored) {
          const tokens = JSON.parse(stored);
          if (tokens?.accessToken) {
            authHeader = { Authorization: `Bearer ${tokens.accessToken}` };
          }
        }
      } catch (e) {
        console.warn("Could not read participant token for update-info", e);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/participants/update-info`,
        {
          method: "POST",
          headers: authHeader,
          body: submitData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Failed to update participant information"
        );
      }

      const result = await response.json();
      setSuccess(true);
      sessionStorage.removeItem("studysync_participant_web_tokens");
      analyticsService.trackParticipantEnrollmentCompleted({
        dataSource: location.state?.dataSource || 'fitbit',
        hasDocuments: formData.documents.length > 0
      });

      // If onSuccess callback is provided (used in multi-step flow), call it
      // Otherwise navigate to success page
      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        // Redirect to success page after delay
        setTimeout(() => {
          navigate("/participant/success", {
            state: {
              participantId: result.participantId || finalParticipantId,
              studyCode: finalStudyCode,
              message: "Your information has been successfully submitted!",
            },
          });
        }, 3000);
      }
    } catch (err: any) {
      setError(err.message || "Failed to update participant information");
    } finally {
      setLoading(false);
    }
  };

  const selectedCountryOption =
    typeof selectedCountryId === "number"
      ? countries.find((country) => country.id === selectedCountryId) || null
      : null;
  const selectedStateOption =
    typeof selectedStateId === "number"
      ? states.find((stateOption) => stateOption.id === selectedStateId) || null
      : null;
  const selectedCityOption =
    typeof selectedCityId === "number"
      ? cities.find((cityOption) => cityOption.id === selectedCityId) || null
      : null;

  if (success) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 8 }}>
          <Paper elevation={3} sx={{ p: 4, textAlign: "center" }}>
            <CheckCircle sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
              Information Submitted Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Thank you for providing your information. You will be redirected
              to the success page shortly.
            </Typography>
            <Alert severity="success" sx={{ mb: 2 }}>
              Your participation in the study is now complete!
            </Alert>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: { xs: 2, sm: 4 } }}>
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 4 } }}>
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Person sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
              Complete Your Profile
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please provide your information to complete your study enrollment
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 3,
                mb: 3,
              }}
            >
              <TextField
                label="First Name"
                name="firstName"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={handleInputChange("firstName")}
                required
                fullWidth
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Last Name"
                name="lastName"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={handleInputChange("lastName")}
                required
                fullWidth
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 3,
                mb: 3,
              }}
            >
              <TextField
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                value={finalEmail}
                onChange={handleInputChange("email")}
                required
                fullWidth
                variant="outlined"
                InputProps={{
                  readOnly: emailPrefilled,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  "aria-readonly": emailPrefilled ? "true" : "false",
                }}
                sx={emailPrefilled ? lockedFieldStyles : undefined}
                helperText={
                  emailPrefilled
                    ? "Email from study enrollment (populated automatically)"
                    : "Enter the email address you used for enrollment"
                }
              />
              <TextField
                label="Phone Number (Optional)"
                name="phoneNumber"
                autoComplete="tel-national"
                value={finalPhoneNumber}
                onChange={handleInputChange("phoneNumber")}
                fullWidth
                variant="outlined"
                placeholder="(000)-0000-000"
                InputProps={{
                  readOnly: phonePrefilled,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  "aria-readonly": phonePrefilled ? "true" : "false",
                }}
                sx={phonePrefilled ? lockedFieldStyles : undefined}
                helperText={
                  phonePrefilled
                    ? "Phone from study enrollment (populated automatically)"
                    : "Enter the phone number you used for enrollment"
                }
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 3,
                mb: 3,
              }}
            >
              <TextField
                label="Age"
                type="number"
                value={formData.age || ""}
                onChange={handleInputChange("age")}
                required
                fullWidth
                variant="outlined"
                inputProps={{ min: "0", max: "150" }}
                InputProps={{
                  startAdornment: (
                    <Cake sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
                helperText="Age in years"
              />
              <TextField
                label="Gender"
                select
                value={formData.gender}
                onChange={handleInputChange("gender")}
                required
                fullWidth
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <Wc sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
                helperText="Select your gender"
              >
                <MenuItem value="">Select Gender</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Box>

            <Typography
              variant="h6"
              sx={{ mb: 2, display: "flex", alignItems: "center" }}
            >
              <LocationOn sx={{ mr: 1 }} />
              Location
            </Typography>

            {locationDataError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {locationDataError}
              </Alert>
            )}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                gap: 2,
                mb: 3,
              }}
            >
              <Autocomplete<LocationOption, false, false, true>
                freeSolo
                openOnFocus
                autoHighlight
                forcePopupIcon
                options={countries}
                value={selectedCountryOption}
                inputValue={formData.country}
                onChange={(_, value) => {
                  void handleCountryChange(value);
                }}
                onInputChange={(_, value, reason) => {
                  if (reason === "input" || reason === "clear") {
                    handleCountryInputChange(value);
                  }
                }}
                loading={countriesLoading}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.name
                }
                isOptionEqualToValue={(option, value) =>
                  typeof value !== "string" && option.id === value.id
                }
                noOptionsText="No country suggestions available"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country (Optional)"
                    variant="outlined"
                    placeholder="Type to search country"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "country-name",
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {countriesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete<LocationOption, false, false, true>
                freeSolo
                openOnFocus
                autoHighlight
                forcePopupIcon
                options={states}
                value={selectedStateOption}
                inputValue={formData.state}
                onChange={(_, value) => {
                  void handleStateChange(value);
                }}
                onInputChange={(_, value, reason) => {
                  if (reason === "input" || reason === "clear") {
                    handleStateInputChange(value);
                  }
                }}
                loading={statesLoading}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.name
                }
                isOptionEqualToValue={(option, value) =>
                  typeof value !== "string" && option.id === value.id
                }
                noOptionsText="No state suggestions available"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State/Province (Optional)"
                    variant="outlined"
                    placeholder="Type to search state"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "address-level1",
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {statesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete<LocationOption, false, false, true>
                freeSolo
                openOnFocus
                autoHighlight
                forcePopupIcon
                options={cities}
                value={selectedCityOption}
                inputValue={formData.city}
                onChange={(_, value) => handleCityChange(value)}
                onInputChange={(_, value, reason) => {
                  if (reason === "input" || reason === "clear") {
                    handleCityInputChange(value);
                  }
                }}
                loading={citiesLoading}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.name
                }
                isOptionEqualToValue={(option, value) =>
                  typeof value !== "string" && option.id === value.id
                }
                noOptionsText="No city suggestions available"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="City (Optional)"
                    variant="outlined"
                    placeholder="Type to search city"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "address-level2",
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {citiesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            <TextField
              label="Additional Information (Optional)"
              value={formData.additionalInfo}
              onChange={handleInputChange("additionalInfo")}
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <Description
                    sx={{
                      mr: 1,
                      color: "text.secondary",
                      alignSelf: "flex-start",
                      mt: 1,
                    }}
                  />
                ),
              }}
              placeholder="Any additional information you'd like to share..."
            />

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{ mb: 2, display: "flex", alignItems: "center" }}
              >
                <AttachFile sx={{ mr: 1 }} />
                Additional Documents (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload any relevant documents (PDF, Word, or image files)
              </Typography>

              <input
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                style={{ display: "none" }}
                id="file-upload"
                multiple
                type="file"
                onChange={handleFileUpload}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<AttachFile />}
                  sx={{ mb: 2 }}
                >
                  Choose Files
                </Button>
              </label>

              {formData.documents.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Selected Files:
                  </Typography>
                  {formData.documents.map((file, index) => (
                    <Chip
                      key={index}
                      label={`${file.name} (${formatFileSize(file.size)})`}
                      onDelete={() => handleRemoveFile(index)}
                      onClick={() => handlePreviewFile(file)}
                      sx={{ mr: 1, mb: 1 }}
                      icon={<Visibility />}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={
                  loading ? <CircularProgress size={20} /> : <CheckCircle />
                }
                sx={{ minWidth: 200 }}
              >
                {loading ? "Submitting..." : "Complete Enrollment"}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>

      {/* File Preview Dialog */}
      <Dialog
        open={previewDialog}
        onClose={() => setPreviewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>File Preview</DialogTitle>
        <DialogContent>
          {previewFile && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {previewFile.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Size: {formatFileSize(previewFile.size)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Type: {previewFile.type}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ParticipantInfoCollection;
